import React, { Dispatch, SetStateAction } from 'react';

import { PhysicianData, ProviderLocation } from '../profile/profile.types';

export type AppointmentType = {
  id: string;
  mhdAppointmentTypeName: string;
  code: string;
  friendlyName: string | null;
  abbreviation: string;
  patientType: string;
  friendlyName2: string;
};

export type AppointmentTypeOption = {
  label: string;
  value: string;
  selected: boolean;
};

export type ViewTypeLink = {
  text: string;
  icon: 'calendar_today' | 'list';
};

export enum VIEW_TYPES {
  TIMESLOTS,
  CALENDAR,
}

export type BookingProps = {
  rendering: {
    uid: string;
    componentName: string;
    // dataSource: string;
    // params: Record<string, unknown>
    fields: {
      notFound: boolean;
      pageTitle: string;
      siteName: string;
      physicianData: PhysicianData;
      physicianFullName: string;
      mhdAppTypes: AppointmentType[];
      siteType: string;
    };
  };
};

// Types for stepper
export enum Status {
  Active = 'active',
  Completed = 'complete',
  Pending = 'pending',
}

export type Step = {
  label: Steps;
  status: Status;
};

export enum Steps {
  One = 'Appointment Information',
  Two = 'Patient Information',
  Three = 'Confirm Information',
}

// step-one Component props
export type StepOneProps = {
  location: ProviderLocation;
  setLocation: Dispatch<SetStateAction<ProviderLocation>>;
  providerLocations: ProviderLocation[];
  fields: {
    physicianData: PhysicianData;
    physicianFullName: string;
    mhdAppTypes: AppointmentType[];
    siteType: string;
  };
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  timeRemainingRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  timeReleasedRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
};

// Appointment search and Timeslot
export type Timeslot = {
  appointmentId: string;
  duration: number;
  startTime: string;
  providerLocationId: string;
  date?: string;
};

export type AppointmentSearch = {
  appointmentTypeId: string | null;
  day: string;
  timeSlots: Timeslot[];
  firstAvailable: string;
};

// ReactQuery and Api
export type MutationRequest =
  | BookAppointmentRequest
  | BookAppointmentSearchRequest
  | BookAppointmentHoldRequest;
export type MutationResponse =
  | BookAppointmentResponse
  | BookAppointmentSearchResponse
  | BookAppointmentHoldResponse;

export type BOOKING_ENDPOINT = 'appointmentSearch' | 'appointmentBook' | 'appointmentHold';

export type BookAppointmentSearchRequest = {
  firstAvailable: boolean;
  newPatient: boolean;
  numberDaysOut: string; // it says 'int' in AppointmentApi 1.0 OAS 3.0 swagger
  providerLocationId?: string | null;
  calendarId?: string | null;
  providerId?: string | null;
  physicianNpi?: string | null;
  timeSlotsNum?: number | null;
  siteType?: string | null;
  source?: string | null;
  startDate?: string | null;
  appointmentTypeIds: string[];
};

export type BookAppointmentSearchResponse = {
  data: AppointmentSearch[];
};

export type BookAppointmentHoldRequest = {
  appointmentId: string;
  appointmentTypeId: string;
  calendarId: string; // same as provider providerLocationId
  date: string; // datestring UNIX eg: '2026-06-04T06:30:00.000Z'
  duration: number;
  providerLocationId: string; // same as calendarId
  startTime: string;
};

export type BookAppointmentHoldResponse = {
  data: {
    appointmentId: string;
    appointmentTypeId: string;
    dateTimeOfAppointment: string;
    // probably most imporant data piece of information we get back,
    holdId: string;
    minutes: string;
    providerLocationId: string | null;
  };
};

export type BookAppointmentRequest = {
  appointmentId: string;
  appointmentTypeId: string;
  dateTimeOfAppointment: string; // this is an ISO string "2026-06-04T06:30:00.000Z"
  holdId: string;
  // !! IMPORTANT NOTE: !! This is really location.mhdCalendarId that is what has to be passed.
  providerLocationId: string; // has to be location.mhdCalendarId
  npi: string;
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    email: string;
    phoneNumber: string;
    parentOrGuardian: string | null;
    relationshipOfGuardian: string;
    reasonForReferral: string;
    middleName: null;
    //  TODO: Check those
    policyId: null;
    patientConfirmationPreference: null;
    secondaryInsuranceId: null;
    secondaryPolicyId: null;
    externalEncounterId: null;
    externalPatientId: null;
  };
  caseID: null;
  referredByName: null;
  referredByPhone: null;
  source: string;
  siteType: string;
  bookingType: string | null;
  address: {
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zip: string;
    //  TODO: Check
    latitude: null;
    longitude: null;
  };
};

export type AppointmentDetails = {
  appointmentId: string;
  appointmentTypeId: string;
  appointmentTypeName: string;
  bookingId: string;
  createdDateTime: string; // Format: YYYYMMDDHHmm
  dateTimeOfAppointment: string; // Format: MM/DD/YYYY hh:mm AM/PM
  id: string;
  informationForConsumer: string;
  minutes: number | null;
  providerLocationId: string;
  slotId: string;
  updatedDateTime: string; // Format: YYYYMMDDHHmm
};

export type BookAppointmentResponse = {
  data: AppointmentDetails;
};

export type ConfirmationData = {
  dateTimeOfAppointment: string;
  appointmentTypeLabel: string;
  reasonForVisit: string;
  location: {
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };
  physician: Pick<
    PhysicianData,
    | 'physicianFirstName'
    | 'physicianMiddleInitial'
    | 'physicianLastName'
    | 'physicianPhotoUrl'
    | 'physicianDesignation'
  > & {
    providerSpecialty: string;
  };
  _response: AppointmentDetails;
};
